import type { SingleDateValue } from '@components/Datepicker'
import { format, isValid, setDate } from 'date-fns'

/**
 * Formats partial date input by automatically adding dots
 * Examples: "05" -> "05.", "0508" -> "05.08.", "050820" -> "05.08.20", "05082024" -> "05.08.2024"
 */
export function formatPartialDate(input: string): string {
  // Remove any existing dots to work with raw numbers
  const numbers = input.replace(/\./g, '')

  // Only process if it's all numbers
  if (!/^\d*$/.test(numbers)) {
    return input
  }

  if (numbers.length <= 2) {
    // Add dot after 2 digits for day
    return numbers.length === 2 ? `${numbers}.` : numbers
  }
  if (numbers.length <= 4) {
    // Add dot after month (4 digits total: dd.mm.)
    return numbers.length === 4
      ? `${numbers.slice(0, 2)}.${numbers.slice(2)}.`
      : `${numbers.slice(0, 2)}.${numbers.slice(2)}`
  }
  if (numbers.length <= 6) {
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 4)}.${numbers.slice(4)}`
  }
  if (numbers.length <= 8) {
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 4)}.${numbers.slice(4, 8)}`
  }

  return input
}

/**
 * Validates if a day exists in a specific month/year combination
 */
function isValidCalendarDate(day: number, month: number, year: number): boolean {
  // Create a date with the given day/month/year
  const testDate = new Date(year, month, day)

  // Check if the date was adjusted (invalid calendar date)
  return (
    testDate.getDate() === day && testDate.getMonth() === month && testDate.getFullYear() === year
  )
}

/**
 * Parses partial date input and returns a Date object if valid partial date is detected
 * Examples: "05." -> sets day to 5, "05.08" -> sets day to 5 and month to 8
 * Only accepts full 4-digit years (YYYY), not 2-digit years
 */
export function parsePartialDate(input: string, currentDate?: SingleDateValue): Date | null {
  const today = new Date()
  const baseDate = currentDate && isValid(currentDate) ? currentDate : today

  // Match different partial date patterns
  const dayPattern = /^(\d{1,2})\.?$/
  const dayMonthPattern = /^(\d{1,2})\.(\d{1,2})\.?$/
  const dayMonthYearPattern = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/ // Only accept 4-digit years

  // Full date (dd.mm.yyyy) - only 4-digit years
  const fullMatch = dayMonthYearPattern.exec(input)
  if (fullMatch) {
    const day = parseInt(fullMatch[1], 10)
    const month = parseInt(fullMatch[2], 10) - 1 // months are 0-indexed
    const year = parseInt(fullMatch[3], 10)

    if (day >= 1 && day <= 31 && month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
      // Validate calendar date before creating Date object
      if (isValidCalendarDate(day, month, year)) {
        const newDate = new Date(year, month, day)
        if (isValid(newDate)) {
          return newDate
        }
      }
    }
  }

  // Day and month (dd.mm)
  const dayMonthMatch = dayMonthPattern.exec(input)
  if (dayMonthMatch) {
    const day = parseInt(dayMonthMatch[1], 10)
    const month = parseInt(dayMonthMatch[2], 10) - 1 // months are 0-indexed

    if (day >= 1 && day <= 31 && month >= 0 && month <= 11) {
      // Validate calendar date before creating Date object
      if (isValidCalendarDate(day, month, baseDate.getFullYear())) {
        const newDate = new Date(baseDate.getFullYear(), month, day)
        if (isValid(newDate)) {
          return newDate
        }
      }
    }
  }

  // Day only (dd.)
  const dayMatch = dayPattern.exec(input)
  if (dayMatch) {
    const day = parseInt(dayMatch[1], 10)

    if (day >= 1 && day <= 31) {
      const newDate = setDate(baseDate, day)
      if (isValid(newDate)) {
        return newDate
      }
    }
  }

  return null
}

/**
 * Parses an ISO date string (YYYY-MM-DD) into a Date object without timezone adjustments.
 * Falls back to standard Date parsing if the format doesn't match YYYY-MM-DD.
 *
 * @param dateStr - The date string to parse
 * @returns Date object or null if parsing fails
 */
export function parseIsoDate(dateStr: string | undefined | null): Date | null {
  if (!dateStr) {
    return null
  }

  // Handle ISO date "YYYY-MM-DD" manually to avoid timezone issues (interpreted as UTC by default)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date
  }

  // Fallback for other formats
  const date = new Date(dateStr)
  if (!Number.isNaN(date.getTime())) {
    return date
  }

  return null
}

/**
 * Reads a date form field, which holds a Date once the user picks one and an ISO
 * date string when the value came from the API.
 *
 * @returns the Date, or undefined when the field is empty or unparseable
 */
export function toDateValue(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return isValid(value) ? value : undefined
  }
  if (typeof value !== 'string') {
    return undefined
  }
  return parseIsoDate(value) ?? undefined
}

/**
 * Writes a date form field or an API payload as an ISO "yyyy-MM-dd" string.
 * Returns an empty string when there is no valid date.
 */
export function formatIsoDate(date: Date | null | undefined): string {
  return date && isValid(date) ? format(date, 'yyyy-MM-dd') : ''
}

/**
 * Formats an ISO date and an optional user name as "dd.mm.yyyy – Name" (Norwegian date separator).
 * Returns an empty string if isoDate is null or undefined.
 */
export function formatPersonModificationDate(
  isoDate: string | null | undefined,
  userName: string | null | undefined,
): string {
  if (!isoDate) return ''
  const date = new Date(isoDate)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const formattedDate = `${day}.${month}.${year}`
  return userName ? `${formattedDate} – ${userName}` : formattedDate
}
